import React from 'react';
import { Button } from '@medely/ui-kit';
import useDialog from '@mp/hooks/useDialog';
import { ReleaseShiftModal } from './releaseShiftModal';
import { ReleaseShiftOptionsProps } from './releaseShiftModal/interface';
import { DROP_OPTION, RELEASE_OPTION, SWAP_OPTION, NO_PROS_TO_SWAP_OPTION } from './constants';
import { isShiftWindowOpen } from './shiftReleaseUtils';
import { useShiftRelease } from './useShiftRelease';
import { useReleaseShiftEvent } from '@mp/hooks/analytics';
import { useSwapProfessionalsList } from './releaseShiftModal/graphql';

type ReleaseShiftButtonProps = {
  jobId?: number | null;
  locationId?: number | null;
};

export const ReleaseShiftButton = ({ jobId, locationId }: ReleaseShiftButtonProps) => {
  const { closeDialog, open, openDialog } = useDialog();
  const { trackReleaseShiftButtonClicked, trackReleaseOptionsModalViewed } = useReleaseShiftEvent();

  const handleOpenDialog = () => {
    trackReleaseShiftButtonClicked(jobId);
    trackReleaseOptionsModalViewed(jobId);
    openDialog();
  };

  const {
    allowDropWindow,
    allowReleaseWindow,
    allowSwapWindow,
    canBeRelease,
    canBeDrop,
    canBeSwap,
    startTime,
    timezone,
  } = useShiftRelease({ jobId: jobId as number });

  const { availableProfessionals } = useSwapProfessionalsList({ jobId, locationId });
  const hasNoProsToSwap = availableProfessionals?.length === 0;

  const isReleaseWindowOpen = isShiftWindowOpen({
    shiftWindow: allowReleaseWindow,
    startTime,
    timezone,
  });
  const isDropWindowOpen = isShiftWindowOpen({
    shiftWindow: allowDropWindow,
    startTime,
    timezone,
  });
  const isSwapWindowOpen = isShiftWindowOpen({
    shiftWindow: allowSwapWindow,
    startTime,
    timezone,
  });

  const canReleaseFinal = canBeRelease && isReleaseWindowOpen;
  const canDropFinal = canBeDrop && isDropWindowOpen;
  const canSwapFinal = canBeSwap && isSwapWindowOpen;

  const isDisabled = !canDropFinal && !canSwapFinal && !canReleaseFinal;

  const swapOption = hasNoProsToSwap ? NO_PROS_TO_SWAP_OPTION : SWAP_OPTION;

  const options = [
    { ...RELEASE_OPTION, disabled: !canReleaseFinal },
    { ...swapOption, disabled: !canSwapFinal },
    { ...DROP_OPTION, disabled: !canDropFinal },
  ] as ReleaseShiftOptionsProps[];

  return (
    <>
      <Button variant="outlined" onClick={handleOpenDialog} disabled={isDisabled} fullWidth>
        Release shift
      </Button>
      <ReleaseShiftModal
        open={open}
        onClose={closeDialog}
        jobId={jobId}
        releaseOptions={options}
        locationId={locationId}
      />
    </>
  );
};
